import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Button,
  Input,
  Field,
  makeStyles,
} from "@fluentui/react-components";
import {
  DataGrid,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridBody,
  DataGridRow,
  DataGridCell,
  TableCellLayout,
  createTableColumn,
} from "@fluentui/react-components";
import { Search20Regular } from "@fluentui/react-icons";

// Components
import CiTypeImage from "../../citypes/components/CiTypeImage";
import ErrorMessageBar from "../../../shared/components/ErrorMessageBar";
import Spinner from "../../../shared/components/Spinner";

// Business logic
import { useCisQuery } from "../hooks/useCiQueries";

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: "900px",
    width: "90vw",
    maxHeight: "80vh",
  },
  dialogBody: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    minHeight: 0,
  },
  searchField: {
    flexShrink: 0,
  },
  gridContainer: {
    flex: 1,
    minHeight: 0,
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  scrollArea: {
    flex: 1,
    overflowY: "auto",
    overflowX: "auto",
    minHeight: 0,
  },
  stickyHeaderRow: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    display: "flex",
  },
});

/**
 * CiSelector - A modal dialog for selecting a CI asset
 * @param {boolean} isOpen - Controls dialog visibility
 * @param {function} onClose - Callback when dialog is closed without selection
 * @param {function} onSelect - Callback when CI is selected, receives selected CI object
 * @param {string} title - Optional dialog title
 */
function CiSelector({ isOpen, onClose, onSelect, title }) {
  const { t } = useTranslation();
  const styles = useStyles();
  const [selectedCi, setSelectedCi] = useState(null);
  const [params, setParams] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState([]);

  const { data = [], isLoading, error, refetch } = useCisQuery(params, (data) => setFilteredData(data), isOpen);

  // Reset selection when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setParams({});
      setSelectedCi(null);
      setSearchTerm("");
    }
  }, [isOpen]);

  const handleSelect = () => {
    if (selectedCi && onSelect) {
      onSelect(selectedCi);
      onClose?.();
    }
  };

  const handleCancel = () => {
    setSelectedCi(null);
    onClose?.();
  };

  const handleInputKeyUp = (e) => {
    if (e.key === "Enter") setParams({ name: searchTerm });
  };

  const columns = [
    createTableColumn({
      columnId: "type",
      renderHeaderCell: () => t("generic.type"),
      renderCell: (item) => (
        <TableCellLayout>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
            <CiTypeImage
              ciType={{
                id: item.type_id,
                public_id: item.public_type_id,
                name: item.type,
                public_icon_id: item.public_icon_id,
              }}
              height={20}
            />
            {item.type}
          </span>
        </TableCellLayout>
      ),
    }),
    createTableColumn({
      columnId: "name",
      renderHeaderCell: () => t("generic.name"),
      renderCell: (item) => <TableCellLayout>{item.name}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "status",
      renderHeaderCell: () => t("generic.status"),
      renderCell: (item) => <TableCellLayout>{item.status}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "sn",
      renderHeaderCell: () => t("generic.serialNumber"),
      renderCell: (item) => <TableCellLayout>{item.sn}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "site",
      renderHeaderCell: () => t("generic.site"),
      renderCell: (item) => <TableCellLayout>{item.site}</TableCellLayout>,
    }),
  ];

  const columnSizingOptions = {
    type: { minWidth: 120, idealWidth: 150 },
    name: { minWidth: 150, idealWidth: 200 },
    status: { minWidth: 100, idealWidth: 120 },
    sn: { minWidth: 120, idealWidth: 150 },
    site: { minWidth: 120, idealWidth: 150 },
  };

  const handleSelectionChange = (event, selectionData) => {
    const ids = [...selectionData.selectedItems];
    const selected = data.find((item) => item.id === ids[0]);
    setSelectedCi(selected || null);
  };

  const handleRowDoubleClick = (item) => {
    setSelectedCi(item);
    if (onSelect) {
      onSelect(item);
      onClose?.();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && handleCancel()}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody className={styles.dialogBody}>
          <DialogTitle>{title || t("ci.selector", { defaultValue: "CI selector" })}</DialogTitle>
          <DialogContent style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: "12px" }}>
            <Field className={styles.searchField} label={t("generic.search", { defaultValue: "Search" })}>
              <Input
                contentBefore={<Search20Regular />}
                placeholder={t("ci.searchByNameTypeSn", { defaultValue: "Search by name, type, serial number..." })}
                defaultValue={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyUp={handleInputKeyUp}
              />
            </Field>

            <div className={styles.gridContainer}>
              {isLoading ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "300px" }}>
                  <Spinner />
                </div>
              ) : error ? (
                <div style={{ padding: "16px" }}>
                  <ErrorMessageBar error={error} onRetry={refetch} />
                </div>
              ) : (
                <DataGrid
                  items={data}
                  columns={columns}
                  columnSizingOptions={columnSizingOptions}
                  selectedItems={selectedCi ? [selectedCi.id] : []}
                  getRowId={(item) => item.id}
                  focusMode="composite"
                  resizableColumns
                  selectionMode="single"
                  onSelectionChange={handleSelectionChange}
                  style={{ minHeight: "300px" }}
                >
                  <DataGridHeader>
                    <DataGridRow
                      className={styles.stickyHeaderRow}
                      selectionCell={{ checkboxIndicator: { "aria-label": "Select row" } }}
                    >
                      {({ renderHeaderCell }) => <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>}
                    </DataGridRow>
                  </DataGridHeader>

                  <div className={styles.scrollArea}>
                    <DataGridBody>
                      {({ item, rowId }) => (
                        <DataGridRow
                          key={rowId}
                          selectionCell={{ checkboxIndicator: { "aria-label": "Select row" } }}
                          onDoubleClick={() => handleRowDoubleClick(item)}
                          style={{ cursor: "pointer" }}
                        >
                          {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
                        </DataGridRow>
                      )}
                    </DataGridBody>
                  </div>
                </DataGrid>
              )}
            </div>

            {data.length === 0 && !isLoading && !error && (
              <div style={{ textAlign: "center", color: "#666", padding: "20px" }}>
                {t("ci.noResults", { defaultValue: "No CIs found" })}
              </div>
            )}
          </DialogContent>
        </DialogBody>
        <DialogActions style={{ marginTop: "16px" }}>
          <Button appearance="primary" disabled={!selectedCi} onClick={handleSelect}>
            {t("generic.select", { defaultValue: "Select" })}
          </Button>
          <Button appearance="secondary" onClick={handleCancel}>
            {t("generic.cancel")}
          </Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
}

export default CiSelector;
